import React, { useEffect, useState } from 'react';
import { observer } from "mobx-react-lite";
import { Table, Button, Card, Modal, Form, Input, Space, Popconfirm, Typography, Spin, Select } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from "react-i18next";
import { useStore } from "../../../stores/root-store-context";
import { OperationType, OperationCategory } from "../../../api/FinanceApiClient";

const { Option } = Select;

const OperationTypeManagement = observer(() => {
    const { t } = useTranslation();
    const { financeStore } = useStore();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [editingOperationType, setEditingOperationType] = useState<OperationType | null>(null);

    useEffect(() => {
        const loadData = async () => {
            await financeStore.getOperationCategories();
            await financeStore.getOperationTypes();
        };
        loadData();
    }, [financeStore]);

    const showAddModal = () => {
        setEditingOperationType(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const showEditModal = (record: OperationType) => {
        setEditingOperationType(record);
        form.setFieldsValue({
            ...record,
            operationCategoryId: record.operationCategoryId
        });
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            
            if (editingOperationType) {
                await financeStore.updateOperationType(editingOperationType.id, values);
            } else {
                await financeStore.addOperationType(values);
            }
            setIsModalVisible(false);
        } catch (error) {
            console.error('Validation failed:', error);
        }
    };

    const handleDelete = async (id: number) => {
        await financeStore.deleteOperationType(id);
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: '10%',
        },
        {
            title: t('Name'),
            dataIndex: 'name',
            key: 'name',
            width: '20%',
        },
        {
            title: t('Description'),
            dataIndex: 'description',
            key: 'description',
            width: '30%',
        },
        {
            title: t('Category'),
            key: 'category',
            width: '20%',
            render: (_: unknown, record: OperationType) => {
                const category = financeStore.operationCategories.find(
                    cat => cat.id === record.operationCategoryId
                );
                return t(category?.name || '');
            },
        },
        {
            title: t('Actions'),
            key: 'actions',
            width: '20%',
            render: (_: unknown, record: OperationType) => (
                <Space size="middle">
                    <Button 
                        type="primary" 
                        icon={<EditOutlined />} 
                        onClick={() => showEditModal(record)}
                    />
                    <Popconfirm
                        title={t('DeleteOperationTypeConfirm')}
                        onConfirm={() => handleDelete(record.id)}
                        okText={t('Yes')}
                        cancelText={t('No')}
                    >
                        <Button 
                            type="primary" 
                            danger 
                            icon={<DeleteOutlined />}
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Spin spinning={financeStore.isLoading}>
            <Card
                title={<Typography.Title level={4}>{t('OperationTypeManagement')}</Typography.Title>}
                extra={
                    <Button 
                        type="primary" 
                        icon={<PlusOutlined />} 
                        onClick={showAddModal}
                    >
                        {t('AddOperationType')}
                    </Button>
                }
            >
                <Table 
                    columns={columns} 
                    dataSource={financeStore.operationTypes} 
                    rowKey="id"
                    pagination={{ pageSize: 10 }}
                />

                <Modal
                    title={editingOperationType ? t('EditOperationType') : t('AddOperationType')}
                    open={isModalVisible}
                    onOk={handleSave}
                    onCancel={handleCancel}
                    
  okText={t("OK")}             
  cancelText={t("Cancel")}     
                    destroyOnClose
                >
                    <Form
                        form={form}
                        layout="vertical"
                    >
                        <Form.Item
                            name="name"
                            label={t('Name')}
                            rules={[{ required: true, message: t('PleaseEnterOperationTypeName') }]}
                        >
                            <Input placeholder={t('OperationTypeNameExample')} />
                        </Form.Item>
                        <Form.Item
                            name="description"
                            label={t('Description')}
                            rules={[{ required: true, message: t('PleaseEnterOperationTypeDescription') }]}
                        >
                            <Input.TextArea placeholder={t('OperationTypeDescriptionExample')} rows={3} />
                        </Form.Item>
                        <Form.Item
                            name="operationCategoryId"
                            label={t('Category')}
                            rules={[{ required: true, message: t('PleaseSelectCategory') }]}
                        >
                            <Select placeholder={t('SelectCategory')}>
                                {financeStore.operationCategories.map(category => (
                                    <Option key={category.id} value={category.id}>
                                        {t(category.name)}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Form>
                </Modal>
            </Card>
        </Spin>
    );
});

export default OperationTypeManagement;